import { API_SECRET, NOTIFICATIONS_READ_URL } from "../config/appConfig";
import { awaitPostUpdateHandoff } from "../sync/apiClient";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

function normalizeScalarId(value) {
  const raw = typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
  if (!raw || raw === "[object Object]") return "";
  return raw.replace(/[^a-zA-Z0-9._:-]/g, "");
}

function normalizeRecipient(session = {}) {
  return {
    account_id: normalizeScalarId(session?.id),
    user_id: normalizeScalarId(session?.userId),
    username: normalizeScalarId(session?.username).toLowerCase(),
    name: String(session?.name || "").trim(),
    role: String(session?.role || "").trim().toLowerCase(),
  };
}

function normalizeNotificationId(value) {
  return String(value || "").trim();
}


const NOTIFICATION_READ_QUEUE_KEY = "fz:notificationReadQueue:v1";
const NOTIFICATION_READ_FETCH_CACHE_MS = 45 * 1000;
const NOTIFICATION_READ_OPERATION_CACHE_MS = 1500;
const NOTIFICATION_READ_BACKOFF_MS = 60 * 1000;
const notificationReadRequestCache = new Map();
const notificationReadInFlight = new Map();
let notificationReadBackoffUntil = 0;

function stableJson(value) {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

function buildRequestSignature({ farmId, recipient, notificationIds = [], notifications = [], operations = [] } = {}) {
  const farm_id = normalizeScalarId(farmId);
  const recipientKey = [recipient?.account_id, recipient?.user_id, recipient?.username, recipient?.role].filter(Boolean).join(":");
  const ids = Array.from(new Set((Array.isArray(notificationIds) ? notificationIds : []).map(normalizeNotificationId).filter(Boolean))).sort();
  const notificationKeys = Array.from(new Set((Array.isArray(notifications) ? notifications : []).map((item) => {
    const normalized = normalizeNotificationForApi(item);
    return normalized ? `${normalized.farmId || farm_id}:${normalized.id}:${(normalized.recipients || []).map((recipientItem) => recipientItem.accountId || recipientItem.userId || recipientItem.username || recipientItem.role || "").sort().join("~")}` : "";
  }).filter(Boolean))).sort();
  const opKeys = Array.from(new Set((Array.isArray(operations) ? operations : []).map((item) => {
    const id = normalizeNotificationId(item?.notification_id || item?.notificationId);
    return id && item?.read ? `${id}:read:${String(item?.read_at || item?.readAt || "").trim()}` : "";
  }).filter(Boolean))).sort();
  return stableJson({ farm_id, recipientKey, ids, notificationKeys, opKeys });
}

function getCachedRequest(signature, ttlMs) {
  const cached = notificationReadRequestCache.get(signature);
  if (!cached || Date.now() - cached.at > ttlMs) {
    notificationReadRequestCache.delete(signature);
    return null;
  }
  return cached.value;
}

function setCachedRequest(signature, value) {
  notificationReadRequestCache.set(signature, { at: Date.now(), value });
  if (notificationReadRequestCache.size > 80) {
    const oldest = Array.from(notificationReadRequestCache.entries()).sort((a, b) => a[1].at - b[1].at).slice(0, 20);
    oldest.forEach(([key]) => notificationReadRequestCache.delete(key));
  }
}

function readPendingQueue() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const raw = window.localStorage.getItem(NOTIFICATION_READ_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (_error) {
    return [];
  }
}

function writePendingQueue(items) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const normalized = Array.isArray(items) ? items.filter(Boolean).slice(-500) : [];
    if (!normalized.length) window.localStorage.removeItem(NOTIFICATION_READ_QUEUE_KEY);
    else window.localStorage.setItem(NOTIFICATION_READ_QUEUE_KEY, JSON.stringify(normalized));
  } catch (_error) {}
}

function queueReadOperations({ farmId, recipient, operations }) {
  const farm_id = normalizeScalarId(farmId);
  if (!farm_id || !Array.isArray(operations) || !operations.length) return;
  const base = readPendingQueue();
  const recipientKey = recipient.account_id || recipient.user_id || recipient.username || "";
  const nextByKey = new Map(base.map((item) => [`${item.farm_id}::${item.recipient_key}::${item.notification_id}`, item]));
  operations.forEach((operation) => {
    const notification_id = normalizeNotificationId(operation.notification_id || operation.notificationId);
    if (!notification_id) return;
    const read_at = String(operation.read_at || operation.readAt || new Date().toISOString()).trim();
    nextByKey.set(`${farm_id}::${recipientKey}::${notification_id}`, {
      farm_id,
      recipient_key: recipientKey,
      notification_id,
      read: true,
      read_at,
    });
  });
  writePendingQueue(Array.from(nextByKey.values()));
}

function takeQueuedReadOperations({ farmId, recipient }) {
  const farm_id = normalizeScalarId(farmId);
  const recipientKeys = [recipient.account_id, recipient.user_id, recipient.username].filter(Boolean);
  if (!farm_id || !recipientKeys.length) return { matching: [], remaining: readPendingQueue() };
  const pending = readPendingQueue();
  const matching = [];
  const remaining = [];
  pending.forEach((item) => {
    if (item?.farm_id === farm_id && recipientKeys.includes(item?.recipient_key)) matching.push(item);
    else remaining.push(item);
  });
  return { matching, remaining };
}

function normalizeNotificationForApi(item = {}) {
  const id = normalizeNotificationId(item?.id || item?.clientId);
  if (!id) return null;
  return {
    ...item,
    id,
    clientId: normalizeNotificationId(item?.clientId || id),
    farmId: normalizeScalarId(item?.farmId || item?.farm_id),
    recipients: Array.isArray(item?.recipients) ? item.recipients.map((recipient) => ({
      accountId: normalizeScalarId(recipient?.accountId || recipient?.account_id),
      userId: normalizeScalarId(recipient?.userId || recipient?.user_id),
      username: normalizeScalarId(recipient?.username).toLowerCase(),
      name: String(recipient?.name || "").trim(),
      role: String(recipient?.role || "").trim().toLowerCase(),
    })).filter((recipient) => recipient.accountId || recipient.userId || recipient.username) : [],
  };
}

function normalizeReadMap(payload) {
  const src = payload && typeof payload === "object" ? payload : {};
  return Object.entries(src).reduce((acc, [rawId, meta]) => {
    const notificationId = normalizeNotificationId(rawId);
    if (!notificationId) return acc;
    const itemMeta = meta && typeof meta === "object" ? meta : {};
    acc[notificationId] = {
      readAt: String(itemMeta.readAt || itemMeta.read_at || "").trim(),
      readStatus: String(itemMeta.readStatus || itemMeta.read_status || (itemMeta.readAt || itemMeta.read_at ? "read" : "unread")).trim().toLowerCase() || "unread",
      recipientKey: String(itemMeta.recipientKey || itemMeta.recipient_key || "").trim(),
      accountId: String(itemMeta.accountId || itemMeta.account_id || "").trim(),
      userId: String(itemMeta.userId || itemMeta.user_id || "").trim(),
      username: String(itemMeta.username || "").trim().toLowerCase(),
      name: String(itemMeta.name || "").trim(),
      senderAccountId: String(itemMeta.senderAccountId || itemMeta.sender_account_id || "").trim(),
      senderUserId: String(itemMeta.senderUserId || itemMeta.sender_user_id || "").trim(),
      senderUsername: String(itemMeta.senderUsername || itemMeta.sender_username || "").trim().toLowerCase(),
      senderName: String(itemMeta.senderName || itemMeta.sender_name || "").trim(),
    };
    return acc;
  }, {});
}

function getRecipientCacheKeys({ farmId, session } = {}) {
  const recipient = normalizeRecipient(session);
  const farm_id = normalizeScalarId(farmId);
  if (!farm_id) return [];
  const recipientKeys = [recipient.account_id, recipient.user_id, recipient.username]
    .map((value) => normalizeScalarId(value).toLowerCase())
    .filter(Boolean);
  const unique = Array.from(new Set(recipientKeys.length ? recipientKeys : ["anon"]));
  return unique.map((recipientKey) => `fz:notificationReadMap:v2:${farm_id}:${recipientKey}`);
}

export function getCachedNotificationReadMap({ farmId, session } = {}) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const keys = getRecipientCacheKeys({ farmId, session });
    if (!keys.length) return {};
    return keys.reduce((acc, key) => {
      try {
        const raw = window.localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : {};
        return mergeNotificationReadMaps(acc, normalizeReadMap(parsed && typeof parsed === "object" ? parsed : {}));
      } catch (_error) {
        return acc;
      }
    }, {});
  } catch (_error) {
    return {};
  }
}

export function setCachedNotificationReadMap({ farmId, session, reads } = {}) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const keys = getRecipientCacheKeys({ farmId, session });
    if (!keys.length) return;
    const merged = mergeNotificationReadMaps(getCachedNotificationReadMap({ farmId, session }), reads || {});
    keys.forEach((key) => {
      try { window.localStorage.setItem(key, JSON.stringify(merged)); } catch (_error) {}
    });
  } catch (_error) {}
}

async function postNotificationRecipientState({ farmId, session, notificationIds = [], notifications = [], operations = [], signal } = {}) {
  const recipient = normalizeRecipient(session);
  const farm_id = normalizeScalarId(farmId);
  const normalizedIds = Array.from(new Set((Array.isArray(notificationIds) ? notificationIds : []).map(normalizeNotificationId).filter(Boolean))).sort();
  const normalizedNotifications = (Array.isArray(notifications) ? notifications : []).map(normalizeNotificationForApi).filter(Boolean);
  const currentOps = (Array.isArray(operations) ? operations : []).map((entry) => {
    const notification_id = normalizeNotificationId(entry?.notificationId || entry?.notification_id);
    if (!notification_id || !entry?.read) return null;
    return {
      notification_id,
      read: true,
      read_at: String(entry?.readAt || entry?.read_at || new Date().toISOString()).trim(),
    };
  }).filter(Boolean);
  if (!farm_id || (!recipient.account_id && !recipient.user_id && !recipient.username)) return { reads: {}, unreadCount: 0 };

  const queued = takeQueuedReadOperations({ farmId: farm_id, recipient });
  const normalizedOps = Array.from(new Map([
    ...queued.matching.map((entry) => [entry.notification_id, { notification_id: entry.notification_id, read: true, read_at: entry.read_at || new Date().toISOString() }]),
    ...currentOps.map((entry) => [entry.notification_id, entry]),
  ]).values());
  const hasWrite = normalizedOps.length > 0;
  const signature = buildRequestSignature({ farmId: farm_id, recipient, notificationIds: normalizedIds, notifications: normalizedNotifications, operations: normalizedOps });
  const cacheTtl = hasWrite ? NOTIFICATION_READ_OPERATION_CACHE_MS : NOTIFICATION_READ_FETCH_CACHE_MS;
  const cachedResult = getCachedRequest(signature, cacheTtl);
  if (cachedResult) return cachedResult;
  const inFlight = notificationReadInFlight.get(signature);
  if (inFlight) return inFlight;

  if (!hasWrite && Date.now() < notificationReadBackoffUntil) {
    return { reads: getCachedNotificationReadMap({ farmId: farm_id, session }), unreadCount: 0, throttled: true };
  }

  const requestPromise = (async () => {
    try {
      await awaitPostUpdateHandoff(4200);
      // v3.9.04: não enviar reads de notificação durante snapshot do bootstrap.
      // O bundle já carrega o estado completo de notifications.
      try {
        const _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null;
        if (_ex?.active && _ex?.phase === "snapshot") {
          return { skipped: true, reason: "bootstrap_exclusive_network_gate" };
        }
      } catch (_) {}
      const response = await traceAndFetch(NOTIFICATIONS_READ_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_SECRET,
          "X-User-Name": recipient.name || recipient.username || "system",
        },
        body: JSON.stringify({
          farm_id,
          ...recipient,
          ids: normalizedIds,
          notifications: normalizedNotifications,
          operations: normalizedOps,
        }),
        signal,
      }, { source: "notifications-read", action: "" });
      if (!response.ok) {
        if (response.status === 429 || response.status === 503) notificationReadBackoffUntil = Date.now() + NOTIFICATION_READ_BACKOFF_MS;
        if (currentOps.length) queueReadOperations({ farmId: farm_id, recipient, operations: currentOps });
        return { reads: {}, unreadCount: 0, errorStatus: response.status };
      }
      const json = await response.json().catch(() => ({}));
      if (normalizedOps.length) writePendingQueue(queued.remaining);
      const reads = normalizeReadMap(json?.data?.reads || json?.reads || {});
      if (Object.keys(reads).length) setCachedNotificationReadMap({ farmId: farm_id, session, reads });
      const result = {
        reads,
        unreadCount: Number(json?.data?.unreadCount ?? json?.unreadCount ?? 0) || 0,
      };
      setCachedRequest(signature, result);
      return result;
    } catch (_error) {
      if (currentOps.length) queueReadOperations({ farmId: farm_id, recipient, operations: currentOps });
      return { reads: {}, unreadCount: 0 };
    } finally {
      notificationReadInFlight.delete(signature);
    }
  })();
  notificationReadInFlight.set(signature, requestPromise);
  return requestPromise;
}


export async function fetchNotificationReadMap({ farmId, session, notificationIds = [], notifications = [], signal } = {}) {
  const cached = getCachedNotificationReadMap({ farmId, session });
  const result = await postNotificationRecipientState({ farmId, session, notificationIds, notifications, signal });
  return mergeNotificationReadMaps(cached, result.reads || {});
}

export async function persistNotificationReadBatch({ farmId, session, operations = [], notifications = [] } = {}) {
  return postNotificationRecipientState({ farmId, session, operations, notifications });
}

export function mergeNotificationReadMaps(base = {}, patch = {}) {
  const next = { ...(base && typeof base === "object" ? base : {}) };
  Object.entries(patch && typeof patch === "object" ? patch : {}).forEach(([rawId, meta]) => {
    const notificationId = normalizeNotificationId(rawId);
    if (!notificationId) return;
    const normalized = meta && typeof meta === "object" ? meta : {};
    const previous = next[notificationId] && typeof next[notificationId] === "object" ? next[notificationId] : {};
    const previousRead = String(previous.readStatus || "").trim().toLowerCase() === "read" || !!String(previous.readAt || "").trim();
    const readAt = String(normalized.readAt || normalized.read_at || "").trim();
    const readStatus = String(normalized.readStatus || normalized.read_status || (readAt ? "read" : "unread")).trim().toLowerCase();
    const shouldBeRead = previousRead || readStatus === "read";
    next[notificationId] = {
      readAt: shouldBeRead ? (readAt || String(previous.readAt || "").trim()) : "",
      readStatus: shouldBeRead ? "read" : "unread",
      recipientKey: String(normalized.recipientKey || normalized.recipient_key || "").trim(),
      accountId: String(normalized.accountId || normalized.account_id || "").trim(),
      userId: String(normalized.userId || normalized.user_id || "").trim(),
      username: String(normalized.username || "").trim().toLowerCase(),
      name: String(normalized.name || "").trim(),
      senderAccountId: String(normalized.senderAccountId || normalized.sender_account_id || "").trim(),
      senderUserId: String(normalized.senderUserId || normalized.sender_user_id || "").trim(),
      senderUsername: String(normalized.senderUsername || normalized.sender_username || "").trim().toLowerCase(),
      senderName: String(normalized.senderName || normalized.sender_name || "").trim(),
    };
  });
  return next;
}
